import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for password verification endpoints - let the component handle it
      const isPasswordVerification = error.config?.url?.includes('/auth/verify-password');
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');
      
      if (!isPasswordVerification && !isLoginEndpoint) {
        // Handle unauthorized access for other endpoints
        localStorage.removeItem('authToken');
        
        // Only redirect if not already on login page to avoid infinite reload
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const login = (credentials) => {
  return api.post('/auth/login', credentials);
};

// export const loginUser = (credentials) => {
//   return api.post('/auth/login', credentials);
// };

export const registerLaboratory = (labData) => {
  return api.post('/auth/register', labData);
};

export const sendOTP = (data) => {
  return api.post('/auth/send-otp', data);
};
export const sendExistingOTP = (data) => {
  return api.post('/auth/send-existing-otp', data);
};

export const verifyOTP = (data) => {
  return api.post('/auth/verify-otp', data);
};
export const verifyExistingOTP = (data) => {
  return api.post('/auth/verify-existing-otp', data);
};

export const checkExistingUser = (data) => {
  return api.post('/auth/check-existing-user', data);
};

export const changePassword = (data) => {
  return api.post('/auth/change-password', data);
};

export const adminResetPassword = (data) => {
  return api.post('/auth/admin-reset-password', data);
};

// Location APIs
export const getCountries = () => {
  return api.get('/location/countries');
};

export const getStates = () => {
  return api.get(`/laboratory/states`);
};

export const getCities = (stateId) => {
  return api.get(`/laboratory/cities/${stateId}`);
};

// Laboratory APIs
export const getAllLabs = () => {
  return api.get('/calibration-lab/labs');
};

export const getLabById = (orgId) => {
  return api.get(`/calibration-lab/labs/${orgId}`);
};

export const saveLabBasicDetails = (labData) => {
  return api.post('/calibration-lab/basic-details', labData);
};

export const updateLab = (orgId, labData) => {
  return api.put(`/calibration-lab/labs/${orgId}`, labData);
};

export const deleteLab = (orgId) => {
  return api.delete(`/calibration-lab/labs/${orgId}`);
};

// User APIs
export const getAllUsers = () => {
  return api.get('/users');
};

export const getUserById = (id) => {
  return api.get(`/users/${id}`);
};

export const createUser = (userData) => {
  return api.post('/users', userData);
};

export const updateUser = (id, userData) => {
  return api.put(`/users/${id}`, userData);
};

export const deleteUser = (id) => {
  return api.delete(`/users/${id}`);
};

// Declaration APIs
export const getApplicationId = (orgId) => {
  return api.post('/declaration/get-app-id', { orgId });
};

export const getCurrentStage = (appId) => {
  return api.post('/declaration/get-current-stage', { appId });
};

export const submitDeclaration = (data) => {
  return api.post('/declaration/submit', data);
};
export const submitTerms = (data) => {
  return api.post('/declaration/terms/submit', data);
};

export const getDeclarationStatus = (orgId) => {
  return api.get(`/declaration/status/${orgId}`);
};

// Application Form APIs
export const getApplicationForm = (labTypeId, orgId) => {
  return api.get(`/application/form?labTypeId=${labTypeId}&orgId=${orgId}`);
};

export const saveApplicationForm = (data) => {
  return api.post('/application/save', data);
};

export const submitApplication = (data) => {
  return api.post('/application/submit', data);
};

// Document Adequacy APIs
export const getAssessmentData = (capacity) => {
  return api.get(`/document-adequacy/assessment?capacity=${capacity || 'LA'}`);
};

export const getAssessorInfo = () => {
  return api.get('/document-adequacy/assessor');
};

export const checkAdequacyStatus = () => {
  return api.get('/document-adequacy/adequacy-status');
};

export const saveAssessmentAnswer = (data) => {
  return api.post('/document-adequacy/save-answer', data);
};

export const submitDAResponse = (data) => {
  return api.post('/document-adequacy/submit-response', data);
};

export const uploadExcelData = (data) => {
  return api.post('/document-adequacy/upload-excel', data);
};

// DAQMS Sub Category APIs
export const getDAQMSSubCategories = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field if not specified
  const field = filters.field || 'Medical';
  params.append('field', field);
  
  if (filters.standard) params.append('standard', filters.standard);
  if (filters.catId) params.append('catId', filters.catId);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-subcategories${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSSubCategoryById = (id) => {
  return api.get(`/document-adequacy/daqms-subcategories/${id}`);
};

export const getDAQMSFields = () => {
  return api.get('/document-adequacy/daqms-fields');
};

export const getDAQMSStandards = () => {
  return api.get('/document-adequacy/daqms-standards');
};

// DAQMS Category APIs
export const getDAQMSCategories = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field if not specified
  const field = filters.field || 'Medical';
  params.append('field', field);
  
  if (filters.standard) params.append('standard', filters.standard);
  if (filters.displayId) params.append('displayId', filters.displayId);
  if (filters.disId) params.append('disId', filters.disId);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-categories${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSCategoryById = (id) => {
  return api.get(`/document-adequacy/daqms-categories/${id}`);
};

export const getDAQMSCategoryNames = (field = 'Medical') => {
  return api.get(`/document-adequacy/daqms-category-names?field=${field}`);
};

// DAQMS Question APIs
export const getDAQMSQuestions = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Medical';
  const standard = filters.standard || '2022';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.subCategoryId) params.append('subCategoryId', filters.subCategoryId);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-questions${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSQuestionById = (id) => {
  return api.get(`/document-adequacy/daqms-questions/${id}`);
};

// NC Statements API
export const getNCStatements = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Medical';
  const standard = filters.standard || '2022';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/nc-statements${queryString ? '?' + queryString : ''}`);
};

// Implementation Data API (from Compliance_Requirements table)
export const getImplementationData = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Medical';
  const standard = filters.standard || '2022';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/implementation-data${queryString ? '?' + queryString : ''}`);
};

// Templates Data API (from ListFormats table)
export const getTemplatesData = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Medical';
  const standard = filters.standard || '2022';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/templates-data${queryString ? '?' + queryString : ''}`);
};

// Assessment Remarks APIs
export const saveAssessmentRemark = (remarkData) => {
  return api.post('/document-adequacy/save-assessment-remark', remarkData);
};

export const getAssessmentRemarks = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.appId) params.append('appId', filters.appId);
  if (filters.orgId) params.append('orgId', filters.orgId);
  if (filters.assessmentId) params.append('assessmentId', filters.assessmentId);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/assessment-remarks${queryString ? '?' + queryString : ''}`);
};

// Evidence APIs
export const saveEvidence = (evidenceData) => {
  return api.post('/document-adequacy/save-evidence', evidenceData);
};

export const getEvidence = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.appId) params.append('appId', filters.appId);
  if (filters.orgId) params.append('orgId', filters.orgId);
  if (filters.assessmentId) params.append('assessmentId', filters.assessmentId);
  if (filters.ncItem) params.append('ncItem', filters.ncItem);
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/evidence${queryString ? '?' + queryString : ''}`);
};

// Payment APIs
export const verifyPassword = (data) => {
  return api.post('/auth/verify-password', data);
};

export const initiatePayment = (paymentData) => {
  return api.post('/payment/initiate', paymentData);
};

export const getPaymentDetails = (appId) => {
  return api.get(`/payment/details/${appId}`);
};

export const submitOfflinePayment = (paymentData) => {
  return api.post('/payment/offline', paymentData);
};

export const getOfflinePaymentDetails = (appId, paymentTypeId) => {
  return api.get(`/payment/offline/${appId}/${paymentTypeId}`);
};

export const checkPaymentStatus = (appId, paymentType) => {
  return api.get(`/payment/status/${appId}/${paymentType}`);
};

export const getLabDetailsForPayment = (appId) => {
  return api.get(`/payment/lab-details/${appId}`);
};

export const submitGSTExemption = (data) => {
  return api.post('/payment/gst-exemption', data);
};

export const checkGSTApprovalStatus = (appId) => {
  return api.get(`/payment/gst-approval/${appId}`);
};

export const generateVirtualAccount = (orgId) => {
  return api.get(`/payment/virtual-account/${orgId}`);
};

export const handlePaymentResponse = (responseData) => {
  return api.post('/payment/response', responseData);
};

export const getInvoiceNumber = (appId) => {
  return api.get(`/payment/invoice/${appId}`);
};

// Update Payment Gateway Tracking on Page Load
export const updatePaymentTrackingOnPageLoad = (paymentData) => {
  return api.post('/payment/update-tracking-on-pageload', paymentData);
};
export const updatePaymentTrackingOnPageLoadAssessment = (paymentData) => {
  return api.post('/payment/update-tracking-on-pageload-assessment', paymentData);
};

// Form153 Lab Details Data Transfer API
export const checkAndTransferLabDetails = (appId, orgId) => {
  return api.post(`/laboratory/check-transfer-data/${appId}/${orgId}`);
};

// Dashboard API
export const getDashboardData = (appId, orgId) => {
  return api.get(`/laboratory/dashboard/${appId}/${orgId}`);
};

// Dashboard API
export const getDashboardDataNew = (orgId) => {
  return api.get(`/laboratory/dashboard-new/${orgId}`);
};

// Accounts Dashboard API
export const getAccountsDashboardStats = () => {
  return api.get('/accounts/dashboard/stats');
};

export const getApplicationStage = (appId) => {
  return api.get(`/laboratory/application-stage/${appId}`);
};

export const getApplicationStageNew = (appId) => {
  return api.get(`/laboratory/application-stage-new/${appId}`);
};

// Lab Name API
export const getLabName = (orgId) => {
  return api.get(`/laboratory/lab-name/${orgId}`);
};

export const getAnnualFee = (orgId) => {
  return api.get(`/laboratory/annual-fee/${orgId}`);
};

// Assessment Date API
export const submitAssessmentDates = (data) => {
  return api.post('/assessment/submit-dates', data);
};

// Admin Dashboard APIs
export const getAdminApplications = () => {
  return api.get('/admin/applications');
};

export const getNonSubmittedApplications = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.labTypeId) params.append('labTypeId', filters.labTypeId);
  if (filters.userName) params.append('userName', filters.userName);
  if (filters.assessmentTypeId) params.append('assessmentTypeId', filters.assessmentTypeId);
  if (filters.labId) params.append('labId', filters.labId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  
  const queryString = params.toString();
  return api.get(`/admin/non-submitted-applications${queryString ? '?' + queryString : ''}`);
};

export const getApplicationFilterOptions = (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.type) params.append('type', filters.type); // 1,2,3 (submitted, non-submitted, blocked)

  const queryString = params.toString();

  return api.get(`/admin/applications/filter-options${queryString ? '?' + queryString : ''}`);
};

export const getSubmittedApplications = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.labTypeId) params.append('labTypeId', filters.labTypeId);
  if (filters.userName) params.append('userName', filters.userName);
  if (filters.assessmentTypeId) params.append('assessmentTypeId', filters.assessmentTypeId);
  if (filters.fromDate) params.append('fromDate', filters.fromDate);
  if (filters.toDate) params.append('toDate', filters.toDate);
  if (filters.stage) params.append('stage', filters.stage);
  if (filters.labId) params.append('labId', filters.labId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  
  const queryString = params.toString();
  return api.get(`/admin/submitted-applications${queryString ? '?' + queryString : ''}`);
};

export const getBlockedApplications = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.labTypeId) params.append('labTypeId', filters.labTypeId);
  if (filters.userName) params.append('userName', filters.userName);
  if (filters.assessmentTypeId) params.append('assessmentTypeId', filters.assessmentTypeId);
  if (filters.labId) params.append('labId', filters.labId);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  
  const queryString = params.toString();
  return api.get(`/admin/blocked-applications${queryString ? '?' + queryString : ''}`);
};

export const getAdminStatistics = () => {
  return api.get('/admin/statistics');
};

export const getDashboardCountsByLabType = (labTypeId) => {
  return api.get(`/admin/dashboard-counts/${labTypeId}`);
};

export const blockApplication = (appId) => {
  return api.post('/admin/block-application', { appId });
};

export const unblockApplication = (appId) => {
  return api.post('/admin/unblock-application', { appId });
};

export const extendApplication = (data) => {
  return api.post('/admin/extend-application', data);
};

// Get filter options for admin dashboard
export const getAdminFilterOptions = (type) => {
  return api.get(`/admin/filter-options/${type}`);
};

// Get all filtered applications for export (no pagination)
export const getAllFilteredApplications = (type, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.labTypeId) params.append('labTypeId', filters.labTypeId);
  if (filters.userName) params.append('userName', filters.userName);
  if (filters.assessmentTypeId) params.append('assessmentTypeId', filters.assessmentTypeId);
  if (filters.labId) params.append('labId', filters.labId);
  if (filters.search) params.append('search', filters.search);
  // Add export flag to get all data without pagination
  params.append('export', 'true');
  
  const queryString = params.toString();
  
  switch(type) {
    case 'submitted':
      return api.get(`/admin/submitted-applications${queryString ? '?' + queryString : ''}`);
    case 'non-submitted':
      return api.get(`/admin/non-submitted-applications${queryString ? '?' + queryString : ''}`);
    case 'blocked':
      return api.get(`/admin/blocked-applications${queryString ? '?' + queryString : ''}`);
    default:
      throw new Error('Invalid application type for export');
  }
};

// Category Management APIs
export const getCategoryList = () => {
  return api.get('/admin/categories');
};

export const addCategory = (categoryData) => {
  return api.post('/admin/category', categoryData);
};

export const updateCategory = (categoryData) => {
  return api.put(`/admin/category/${categoryData.id}`, categoryData);
};

export const deleteCategory = (id) => {
  return api.delete(`/admin/category/${id}`);
};

export const getStandardsByField = (field) => {
  return api.get(`/admin/standards/${field}`);
};

// Sub-Category Management APIs
export const getSubCategoryList = () => {
  return api.get('/admin/sub-categories');
};

export const addSubCategory = (subCategoryData) => {
  return api.post('/admin/sub-category', subCategoryData);
};

export const updateSubCategory = (subCategoryData) => {
  return api.put(`/admin/sub-category/${subCategoryData.id}`, subCategoryData);
};

export const deleteSubCategory = (id) => {
  return api.delete(`/admin/sub-category/${id}`);
};

export const getCategoriesByFieldAndStandard = (field, standard) => {
  return api.get(`/admin/categories-by-field-standard?field=${field}&standard=${standard}`);
};

// Questions Management APIs
export const getQuestionsList = () => {
  return api.get('/admin/questions');
};

export const addQuestion = (questionData) => {
  return api.post('/admin/question', questionData);
};

export const updateQuestion = (questionData) => {
  return api.put(`/admin/question/${questionData.id}`, questionData);
};

export const deleteQuestion = (id) => {
  return api.delete(`/admin/question/${id}`);
};

export const getSubCategoriesByCategoryId = (categoryId) => {
  return api.get(`/admin/sub-categories-by-category/${categoryId}`);
};

// Compliance Management APIs
export const getComplianceList = () => {
  return api.get('/admin/compliance');
};

export const addCompliance = (complianceData) => {
  return api.post('/admin/compliance', complianceData);
};

export const updateCompliance = (complianceData) => {
  return api.put(`/admin/compliance/${complianceData.id}`, complianceData);
};

export const deleteCompliance = (id) => {
  return api.delete(`/admin/compliance/${id}`);
};

export const getDisplayIdsByCategory = (categoryId) => {
  return api.get(`/admin/display-ids-by-category/${categoryId}`);
};

// List/Form Management APIs
export const getListFormList = () => {
  return api.get('/admin/listform');
};

export const addListForm = (payload) => {
  return api.post('/admin/listform', payload);
};

export const updateListForm = (payload) => {
  return api.put(`/admin/update-listform`, payload)
};

export const deleteListForm = (id) => {
  return api.delete(`/admin/listform/${id}`);
};

// NC Statements APIs
export const getNCStatementsList = () => {
  return api.get('/admin/ncstatements');
};

export const addNCStatement = (data) => {
  return api.post('/admin/ncstatements', data);
};

export const updateNCStatement = (data) => {
  return api.put(`/admin/ncstatements/${data.id}`, data);
};

export const deleteNCStatement = (id) => {
  return api.delete(`/admin/ncstatements/${id}`);
};

// Assessment Details APIs
export const getAssessorsForAssessment = (appId, assessmentType = 'Main') => {
  return api.get(`/assessor/assessment/${appId}`, {
    params: { assessmentType }
  });
};

export const updateAssessorStatus = (data) => {
  return api.post('/assessor/assessment/update-status', data);
};

export const submitAssessorAcceptanceToNABL = (data) => {
  return api.post('/assessor/assessment/submit-to-nabl', data);
};

export const getAssessorProfile = (allotedToId) => {
  return api.get(`/assessor/profile/${allotedToId}`);
};

// Scrutiny Remarks Management APIs
export const getScrutinyRemarks = (filters = {}) => {
  return api.get('/scrutiny-remarks', { params: filters });
};

export const getClauseNo = (filters = {}) => {
  return api.get('https://nablwpapi.qci.org.in/api/main/GetClauseNo', { params: filters });
};

export const getScrutinyRemarkById = (id) => {
  return api.get(`/scrutiny-remarks/${id}`);
};

export const createScrutinyRemark = (data) => {
  return api.post('/scrutiny-remarks', data);
};

export const updateScrutinyRemark = (id, data) => {
  return api.put(`/scrutiny-remarks/${id}`, data);
};

export const deleteScrutinyRemark = (id) => {
  return api.delete(`/scrutiny-remarks/${id}`);
};

export const getSectionNames = () => {
  return api.get('/scrutiny-remarks/section-names');
};

export const getScrutinyRemarksStatistics = () => {
  return api.get('/scrutiny-remarks/statistics');
};

export const bulkCreateScrutinyRemarks = (data) => {
  return api.post('/scrutiny-remarks/bulk', data);
};

// Assessor Management APIs
export const getAllAssessors = (filters = {}) => {
  return api.get('/admin/assessors', { params: filters });
};

export const getAssessorById = (id) => {
  return api.get(`/admin/assessors/${id}`);
};

export const updateAssessorDetails = (Id, data) => {
  return api.put(`/admin/assessors/${Id}`, data);
};

export const deleteAssessor = (id) => {
  return api.delete(`/admin/assessors/${id}`);
};

export const getAssessorStatistics = () => {
  return api.get('/admin/assessors/statistics');
};

export const toggleAssessorStatus = (id, data) => {
  return api.patch(`/admin/assessors/${id}/status`, data);
};

// Accounts Download API
export const downloadAccountsReport = (params) => {
  return api.get('/accounts/download-report', {
    params,
    responseType: 'blob'
  });
};

// JD-DO Mapping API
export const getJDDOMappingList = (params = {}) => {
  return api.get('/admin/jd-do-mapping', { params });
};

export const addJDDOMapping = (data) => {
  return api.post('/admin/jd-do-mapping', data);
};

export const updateJDDOMapping = (data) => {
  return api.put('/admin/jd-do-mapping', data);
};

export const deleteJDDOMapping = (id) => {
  return api.delete(`/admin/jd-do-mapping/${id}`);
};

export const getJDList = () => {
  return api.get('/admin/jd-list');
};

export const getDOList = (jdId) => {
  return api.get(`/admin/do-list/${jdId}`);
};

// Assessment Types API
export const getAssessmentTypes = () => {
  return api.get('/change-name-app/assessment-types');
};

export default api;
